import { useCallback, useEffect, useRef, useState } from 'react';
import { playSound } from '../../utils/utils';
import { getBoardSquareColor, getBoardSquareImpactLabel } from './doorBoard';
import './SabotageCutscene.css';

const STAGE_COPY = {
  targeting: 'TARGETING CORE ADVANTAGES',
  criticals: 'CRITICAL HIT  →  MISS',
  modifiers: 'INVERTING DAMAGE BONUSES',
  locked: 'SABOTAGE COMPLETE · NEXT CORE THROW',
};

const getSquareText = (square) =>
  getBoardSquareImpactLabel({ ...square, coreDamage: true });

const SabotageCutscene = ({
  board = [],
  pending,
  reduceMotion = false,
  onComplete,
  onSkip,
}) => {
  const [stage, setStage] = useState('targeting');
  const headingRef = useRef(null);
  const skipButtonRef = useRef(null);
  const playedSoundRef = useRef(null);
  const completeRef = useRef(onComplete);
  const skipRef = useRef(onSkip);
  const finishedRef = useRef(false);
  const changesByIndex = new Map(
    (pending.changes || []).map((change) => [change.index, change])
  );
  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    completeRef.current?.();
  }, []);
  const skipAnimation = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    skipRef.current?.();
  };
  const playTransformationSound = useCallback(() => {
    if (playedSoundRef.current === pending.id) return;
    playedSoundRef.current = pending.id;
    playSound('/sounds/sabotage-board.mp3');
  }, [pending.id]);

  useEffect(() => {
    completeRef.current = onComplete;
    skipRef.current = onSkip;
  }, [onComplete, onSkip]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    headingRef.current?.focus({ preventScroll: true });
    finishedRef.current = false;
    setStage('targeting');
    const timers = [];
    const schedule = (callback, delay) => {
      timers.push(window.setTimeout(callback, delay));
    };

    if (reduceMotion) {
      setStage('locked');
      playTransformationSound();
      schedule(finish, 300);
    } else {
      schedule(() => {
        setStage('criticals');
        playTransformationSound();
      }, 700);
      schedule(() => setStage('modifiers'), 1450);
      schedule(() => setStage('locked'), 2350);
      schedule(finish, 3300);
    }

    return () => {
      timers.forEach(window.clearTimeout);
      if (previousFocus?.isConnected) previousFocus.focus?.();
    };
  }, [finish, pending.id, playTransformationSound, reduceMotion]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      skipAnimation();
      return;
    }
    if (event.key !== 'Tab') return;
    const first = headingRef.current;
    const last = skipButtonRef.current;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const getVisibleSquare = (change, index) => {
    if (!change) return board[index];
    if (stage === 'targeting') return change.before;
    if (stage === 'criticals' && !change.categories.includes('critical'))
      return change.before;
    return change.after;
  };
  const criticalCount = pending.criticalCount || 0;
  const modifierCount = pending.modifierCount || 0;
  const modifierSummary = [
    ...new Set(
      (pending.changes || [])
        .filter((change) => change.categories.includes('modifier'))
        .map(
          ({ before, after }) =>
            `${before.modifier > 0 ? '+' : ''}${before.modifier} → ${
              after.modifier
            }`
        )
    ),
  ].join(' · ');

  return (
    <div className="coreboundSabotageBackdrop">
      <section
        className={`coreboundSabotageDialog coreboundSabotageStage-${stage}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="corebound-sabotage-title"
        aria-describedby="corebound-sabotage-description"
        onKeyDown={handleKeyDown}
      >
        <div className="coreboundSabotageEyebrow">
          <span aria-hidden="true">⌖</span> CORE ARENA · SABOTAGE
        </div>
        <h2 id="corebound-sabotage-title" ref={headingRef} tabIndex={-1}>
          Corrupting the Core Board
        </h2>
        <p
          className="coreboundSabotageStageCopy"
          role="status"
          aria-live="polite"
        >
          {STAGE_COPY[stage]}
        </p>
        {stage === 'modifiers' && modifierSummary && (
          <p className="coreboundSabotageEquation">{modifierSummary}</p>
        )}
        <p
          className="coreboundSabotageDescription"
          id="corebound-sabotage-description"
        >
          {criticalCount} critical hit tiles targeted · {modifierCount} positive
          HP damage modifiers reversed
        </p>

        <div
          className="coreboundSabotageBoard"
          role="img"
          aria-label={`Preview of the Core's five by five board. Sabotage changes ${criticalCount} Critical Hit tiles into Critical Misses and reverses ${modifierCount} positive modifiers.`}
        >
          {board.map((_, index) => {
            const change = changesByIndex.get(index);
            const visibleSquare = getVisibleSquare(change, index);
            const isChanging = !!change;
            const isCriticalChange = change?.categories.includes('critical');
            const isModifierChange = change?.categories.includes('modifier');
            const isConverted =
              stage === 'locked' ||
              stage === 'modifiers' ||
              (stage === 'criticals' && isCriticalChange);
            const isActiveChange =
              stage === 'targeting'
                ? isChanging
                : stage === 'criticals'
                ? isCriticalChange
                : stage === 'modifiers'
                ? isModifierChange
                : false;

            return (
              <span
                className={`coreboundSabotageCell${
                  isChanging ? ' is-targeted' : ''
                }${isActiveChange ? ' is-changing' : ''}${
                  isConverted ? ' is-corrupted' : ''
                }`}
                key={index}
                style={{
                  '--sabotage-accent': getBoardSquareColor(visibleSquare),
                }}
              >
                <small>{index + 1}</small>
                <strong>{getSquareText(visibleSquare)}</strong>
              </span>
            );
          })}
        </div>

        <p className="coreboundSabotagePromise">
          This updated board is the exact board the Core will use on its next
          throw.
        </p>
        <button
          ref={skipButtonRef}
          className="coreboundSabotageSkip"
          type="button"
          onClick={stage === 'locked' ? finish : skipAnimation}
        >
          {stage === 'locked' ? 'Continue' : 'Skip animation'}
        </button>
      </section>
    </div>
  );
};

export default SabotageCutscene;
